import { NoteEventBox, NoteEventCollectionBox } from "@opendaw/studio-boxes";
import { asDefined, Intervals, Iterables, Notifier, Terminator, UUID } from "@opendaw/lib-std";
import { EventCollection, NoteEvent } from "@opendaw/lib-dsp";
import { Pointers } from "@opendaw/studio-enums";
import { NoteEventBoxAdapter } from "../event/NoteEventBoxAdapter";
export class NoteEventCollectionBoxAdapter {
    #terminator = new Terminator();
    #context;
    #box;
    #changeNotifier;
    #adapters;
    #events;
    #minPitch = 60;
    #maxPitch = 60;
    #maxDuration = 0;
    #computedExtremas = false;
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#changeNotifier = new Notifier();
        this.#adapters = UUID.newSet(adapter => adapter.uuid);
        this.#events = EventCollection.create(NoteEvent.Comparator);
        const addNoteProcedure = (box) => {
            const adapter = asDefined(box.accept({
                visitNoteEventBox: (box) => this.#context.boxAdapters.adapterFor(box, NoteEventBoxAdapter)
            }), `Could not find adapter for ${box}`);
            if (this.#adapters.add(adapter)) {
                this.#events.add(adapter);
                this.#onEventsChanged();
            }
        };
        this.#box.events.pointerHub.incoming().forEach(({ box }) => addNoteProcedure(box));
        this.#terminator.own(this.#box.events.pointerHub.subscribe({
            onAdded: ({ box }) => addNoteProcedure(box),
            onRemoved: ({ box: { address: { uuid } } }) => {
                this.#events.remove(this.#adapters.removeByKey(uuid));
                this.#onEventsChanged();
            }
        }));
        this.#terminator.own(this.#box.owners.pointerHub.subscribe({
            onAdded: () => this.#changeNotifier.notify(this),
            onRemoved: () => this.#changeNotifier.notify(this)
        }));
    }
    copy() {
        const graph = this.#context.boxGraph;
        const boxCopy = NoteEventCollectionBox.create(graph, UUID.generate());
        this.#events.asArray().forEach(source => source.copyTo({ events: boxCopy.events }));
        return this.#context.boxAdapters.adapterFor(boxCopy, NoteEventCollectionBoxAdapter);
    }
    createEvent({ position, duration, velocity, pitch, chance, playCount, cent }) {
        return this.#context.boxAdapters.adapterFor(NoteEventBox.create(this.#context.boxGraph, UUID.generate(), box => {
            box.position.setValue(position);
            box.duration.setValue(duration);
            box.velocity.setValue(velocity);
            box.pitch.setValue(pitch);
            box.chance.setValue(chance);
            box.playCount.setValue(playCount);
            box.cent.setValue(cent);
            box.events.refer(this.#box.events);
        }), NoteEventBoxAdapter);
    }
    subscribeChange(observer) { return this.#changeNotifier.subscribe(observer); }
    selectable() { return this.#events.asArray(); }
    selectableAt(coordinates) {
        for (const element of this.#events.asArray()) { // TODO Use optimized data structures
            if (element.position <= coordinates.u && coordinates.u < element.complete && element.pitch === coordinates.v) {
                return Iterables.one(element);
            }
        }
        return Iterables.empty();
    }
    selectablesBetween(begin, end) {
        const events = [];
        for (const element of this.#events.asArray()) { // TODO Use optimized data structures
            if (Intervals.intersect1D(element.position, element.complete, begin.u, end.u)
                && Intervals.intersect1D(element.pitch, element.pitch, begin.v, end.v)) {
                events.push(element);
            }
        }
        return events;
    }
    requestSorting() {
        this.#events.onIndexingChanged();
        this.onEventPropertyChanged();
    }
    onEventPropertyChanged() { this.#onEventsChanged(); }
    terminate() { this.#terminator.terminate(); }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get numOwners() { return this.#box.owners.pointerHub.filter(Pointers.NoteEventCollection).length; }
    get events() {
        if (!this.#computedExtremas) {
            this.#computeExtremas();
        }
        return this.#events;
    }
    get minPitch() {
        if (!this.#computedExtremas) {
            this.#computeExtremas();
        }
        return this.#minPitch;
    }
    get maxPitch() {
        if (!this.#computedExtremas) {
            this.#computeExtremas();
        }
        return this.#maxPitch;
    }
    get maxDuration() {
        if (!this.#computedExtremas) {
            this.#computeExtremas();
        }
        return this.#maxDuration;
    }
    toString() { return `{NoteEventCollectionBox ${UUID.toString(this.#box.address.uuid)}}`; }
    #onEventsChanged() {
        this.#computedExtremas = false;
        this.#changeNotifier.notify(this);
    }
    #computeExtremas() {
        let min = 127 | 0;
        let max = 0 | 0;
        let maxDuration = 0;
        this.#events.asArray().forEach(({ pitch, duration }) => {
            min = Math.min(min, pitch);
            max = Math.max(max, pitch);
            maxDuration = Math.max(maxDuration, duration);
        });
        this.#minPitch = min;
        this.#maxPitch = max;
        this.#maxDuration = maxDuration;
        this.#computedExtremas = true;
    }
}
